import { putSignedFile } from '../../xomics/api';
import { WEBSERVER_API_ORIGIN } from '../../config/apiOrigins';

export const XOMICS_WEBSERVER_ORIGIN = WEBSERVER_API_ORIGIN;
const credentialRegistrations = new Map();

export class WebserverApiError extends Error {
  constructor(message, { status = 0, code = null, body = null } = {}) {
    super(message);
    this.name = 'WebserverApiError';
    this.status = status;
    this.code = code;
    this.body = body;
  }
}

function cleanServiceCode(serviceCode) {
  const code = String(serviceCode || '').trim().toLowerCase();
  if (!/^[a-z0-9-]{2,64}$/.test(code)) throw new Error('Invalid webserver service code');
  return code;
}

export function serviceApiBase(serviceCode) {
  return `${XOMICS_WEBSERVER_ORIGIN}/api/${cleanServiceCode(serviceCode)}`;
}

async function jsonBody(response) {
  const type = response.headers.get('content-type') || '';
  if (!type.includes('application/json')) return null;
  return response.json();
}

export async function serviceRequest(serviceCode, token, path, options = {}) {
  await registerWebserverToken(token);
  const headers = new Headers(options.headers || {});
  headers.set('Accept', 'application/json');
  headers.set('X-Webserver-Token', String(token || ''));
  if (options.body !== undefined && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }
  const response = await fetch(`${serviceApiBase(serviceCode)}${path}`, {
    ...options,
    headers,
  });
  const body = await jsonBody(response);
  if (!response.ok) {
    throw new WebserverApiError(
      body?.error || `Request failed (${response.status})`,
      { status: response.status, code: body?.code || null, body },
    );
  }
  return body || {};
}

export function registerWebserverToken(token) {
  const value = String(token || '').trim();
  if (!value) return Promise.reject(new Error('A webserver credential is required'));
  if (credentialRegistrations.has(value)) return credentialRegistrations.get(value);
  const request = fetch(`${XOMICS_WEBSERVER_ORIGIN}/auth/register`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token: value }),
  }).then(async (response) => {
    const body = await jsonBody(response);
    if (!response.ok) {
      throw new WebserverApiError(
        body?.error || `Credential registration failed (${response.status})`,
        { status: response.status, code: body?.code || null, body },
      );
    }
    return body || {};
  }).catch((error) => {
    credentialRegistrations.delete(value);
    throw error;
  });
  credentialRegistrations.set(value, request);
  return request;
}

export async function webserverFetch(url, token, options = {}) {
  await registerWebserverToken(token);
  const headers = new Headers(options.headers || {});
  headers.set('X-Webserver-Token', String(token || ''));
  return fetch(url, { ...options, headers });
}

export function pipelineKey(pipeline) {
  return pipeline.mode ? `${pipeline.name}:${pipeline.mode}` : pipeline.name;
}

export async function loadServiceCatalogue(serviceCode, token) {
  return serviceRequest(serviceCode, token, '/catalogue');
}

export function defaultServicePipeline(catalogue) {
  const pipelines = Array.isArray(catalogue?.pipelines) ? catalogue.pipelines : [];
  const selected = pipelines.find((pipeline) => Number(pipeline.is_default) === 1)
    || (pipelines.length === 1 ? pipelines[0] : null);
  if (!selected) {
    const unavailable = Array.isArray(catalogue?.unavailable_mappings)
      ? catalogue.unavailable_mappings : [];
    const suffix = unavailable.length
      ? ` The configured pipeline (${unavailable.join(', ')}) is not in the active release.`
      : '';
    throw new Error(`No active pipeline is available for this service.${suffix}`);
  }
  return selected;
}

export function uploadFileEntries(pipeline, selectedFiles) {
  const key = pipelineKey(pipeline);
  const entries = [];
  let position = 1;
  for (const selected of selectedFiles) {
    if (!selected?.file || !Number.isSafeInteger(Number(selected.slotPosition))) continue;
    entries.push({
      position,
      original_name: selected.file.name,
      declared_bytes: selected.file.size,
      fills: [`${key}:${Number(selected.slotPosition)}`],
      file: selected.file,
      label: selected.label || selected.file.name,
    });
    position += 1;
  }
  return entries;
}

export async function createServiceUpload({
  serviceCode,
  token,
  pipeline,
  sampleCode,
  selectedFiles,
  patientParams = null,
  pipelineParams = null,
  displayName = null,
  metadata = null,
  onProgress = null,
}) {
  const entries = uploadFileEntries(pipeline, selectedFiles);
  if (entries.length === 0) throw new Error('At least one input file is required');

  const created = await serviceRequest(serviceCode, token, '/uploads', {
    method: 'POST',
    body: JSON.stringify({
      files: entries.map(({ file, label, ...entry }) => entry),
      pipelines: [Number(pipeline.id)],
      sample_code: sampleCode,
      patient_params: patientParams,
      pipeline_params: pipelineParams ? { [pipeline.id]: pipelineParams } : null,
      display_name: displayName,
      metadata,
    }),
  });

  const byPosition = new Map(entries.map((entry) => [Number(entry.position), entry]));
  for (const plan of created.files || []) {
    const entry = byPosition.get(Number(plan.position));
    if (!entry) throw new Error(`The upload plan returned an unknown file position (${plan.position})`);
    // eslint-disable-next-line no-await-in-loop
    const parts = await putSignedFile(plan, entry.file, (fraction) => {
      if (onProgress) onProgress({
        position: Number(plan.position),
        label: entry.label,
        fraction,
      });
    });
    // eslint-disable-next-line no-await-in-loop
    await serviceRequest(
      serviceCode,
      token,
      `/uploads/${encodeURIComponent(created.uuid)}/files/${plan.position}/assemble`,
      { method: 'POST', body: JSON.stringify({ parts }) },
    );
  }

  const completed = await serviceRequest(
    serviceCode,
    token,
    `/uploads/${encodeURIComponent(created.uuid)}/complete`,
    { method: 'POST', body: JSON.stringify({}) },
  );
  return { created, completed };
}

export function webserverJobStatus(job, upload) {
  const value = String(job?.run_status || job?.state || upload?.status || '').toLowerCase();
  if (value === 'success' || value === 'succeeded' || value === 'complete') return 'completed';
  if (value === 'queued' || value === 'pending' || value === 'ready') return 'queued';
  if (['assigned', 'downloading', 'running', 'offloading'].includes(value)) return 'running';
  if (value === 'cancelled') return 'cancelled';
  if (value === 'failed' || upload?.status === 'failed') return 'failed';
  return value || 'pending_upload';
}

export function flattenServiceUploads(uploads) {
  return (uploads || []).flatMap((upload) => {
    const jobs = upload.jobs?.length ? upload.jobs : [null];
    return jobs.map((job) => ({
      job_id: upload.uuid,
      jobId: upload.uuid,
      job_uuid: job?.uuid || null,
      upload_uuid: upload.uuid,
      sample_name: upload.submission?.display_name || job?.display_name || 'Untitled analysis',
      sampleName: upload.submission?.display_name || job?.display_name || 'Untitled analysis',
      sample_code: upload.sample_code,
      gender: upload.submission?.metadata?.gender || upload.submission?.metadata?.sex || null,
      age: upload.submission?.metadata?.age ?? null,
      date_of_birth: upload.submission?.metadata?.date_of_birth || null,
      status: webserverJobStatus(job, upload),
      message: job?.error_message || upload.error_message || null,
      pipeline: job?.pipeline || null,
      mode: job?.mode || '',
      pipeline_display_name: job?.display_name || null,
      code_version: job?.code_version || null,
      estimated_runtime_minutes: Number(job?.est_duration_minutes || 0),
      created_at: Number(job?.created_at || upload.created_at || 0),
      queued_at: job?.queued_at,
      started_at: job?.run_started_at,
      finished_at: job?.run_finished_at,
      progress: webserverJobStatus(job, upload) === 'completed' ? 100 : null,
      submission: upload.submission || null,
      metadata: upload.submission?.metadata || null,
      rawUpload: upload,
      rawJob: job,
    }));
  });
}

export async function loadServiceJobs(serviceCode, token, { limit = 50, beforeId = null } = {}) {
  const query = new URLSearchParams({ limit: String(limit) });
  if (beforeId) query.set('beforeId', String(beforeId));
  const body = await serviceRequest(serviceCode, token, `/jobs?${query.toString()}`);
  return {
    jobs: flattenServiceUploads(body.uploads),
    uploads: body.uploads || [],
    nextBeforeId: body.nextBeforeId || null,
  };
}

export async function openJobDownload(serviceCode, token, job) {
  if (!job?.upload_uuid || !job?.job_uuid) throw new Error('This job has no downloadable run yet');
  const body = await serviceRequest(
    serviceCode,
    token,
    `/uploads/${encodeURIComponent(job.upload_uuid)}`
      + `/jobs/${encodeURIComponent(job.job_uuid)}/download`,
  );
  if (!body.downloadUrl) throw new Error('The result bundle is not available yet');
  window.open(body.downloadUrl, '_blank', 'noopener,noreferrer');
  return body;
}

// ---------------------------------------------------------------- contract
// Every parameter a pipeline's own declaration names comes with the catalogue,
// with who supplies it: the worker, a patient fact, or the caller. A form
// renders the caller's parameters and the patient facts from that, and sends
// them under the pipeline's own names; nothing here keeps a copy of any list.

export function hasParameterContract(pipeline) {
  return Array.isArray(pipeline?.parameters);
}

export function callerParameters(pipeline) {
  return hasParameterContract(pipeline)
    ? pipeline.parameters.filter((parameter) => parameter.supplied_by === 'caller') : [];
}

export function patientParameters(pipeline) {
  return hasParameterContract(pipeline)
    ? pipeline.parameters.filter((parameter) => parameter.supplied_by === 'patient') : [];
}

function filled(value) {
  return value !== undefined && value !== null && String(value).trim() !== '';
}

// values: { parameters: { NAME: value }, facts: { patient_field: value } }
export function contractSubmission(pipeline, values = {}) {
  const pipelineParams = {};
  for (const parameter of callerParameters(pipeline)) {
    const value = values.parameters?.[parameter.name];
    if (filled(value)) pipelineParams[parameter.name] = String(value).trim();
  }
  const patientParams = {};
  for (const parameter of patientParameters(pipeline)) {
    const value = values.facts?.[parameter.patient_field];
    if (filled(value)) patientParams[parameter.patient_field] = String(value).trim();
  }
  return {
    pipelineParams: Object.keys(pipelineParams).length ? pipelineParams : null,
    patientParams: Object.keys(patientParams).length ? patientParams : null,
  };
}

// Labels of what must still be filled before the upload can be created.
export function contractMissing(pipeline, values = {}) {
  const missing = [];
  for (const parameter of patientParameters(pipeline)) {
    if (!filled(values.facts?.[parameter.patient_field])) missing.push(parameter.patient_field);
  }
  for (const parameter of callerParameters(pipeline)) {
    const hasDefault = filled(parameter.default);
    if (parameter.required && !hasDefault && !filled(values.parameters?.[parameter.name])) {
      missing.push(parameter.title || parameter.name);
    }
  }
  return missing;
}

// ---------------------------------------------------------------- reports
// Which report types a job can produce is the binding XOmics holds for its
// pipeline; the catalogue lists it as report_types and /reports/options
// returns it with the available languages.

function jobPath(job) {
  return `/uploads/${encodeURIComponent(job.upload_uuid)}/jobs/${encodeURIComponent(job.job_uuid)}`;
}

export function loadReportOptions(serviceCode, token, job) {
  return serviceRequest(serviceCode, token, `${jobPath(job)}/reports/options`);
}

export function requestReport(serviceCode, token, job, body) {
  return serviceRequest(serviceCode, token, `${jobPath(job)}/reports`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export async function loadLatestReport(serviceCode, token, job) {
  try {
    const body = await serviceRequest(serviceCode, token, `${jobPath(job)}/reports/latest`);
    return body.report || null;
  } catch (error) {
    if (error instanceof WebserverApiError && error.code === 'REPORT_NOT_FOUND') return null;
    throw error;
  }
}

export async function signedReportDownload(serviceCode, token, job, reportUuid) {
  const body = await serviceRequest(
    serviceCode,
    token,
    `${jobPath(job)}/reports/${encodeURIComponent(reportUuid)}/download`,
  );
  if (!body.downloadUrl) throw new Error('The report is not available for download yet');
  return body;
}

// ---------------------------------------------------------------- demos
// Public demo analyses: readable by any visitor, without a credential.

async function publicRequest(serviceCode, path) {
  const response = await fetch(`${serviceApiBase(serviceCode)}${path}`, {
    headers: { Accept: 'application/json' },
  });
  const body = await jsonBody(response);
  if (!response.ok) {
    throw new WebserverApiError(
      body?.error || `Request failed (${response.status})`,
      { status: response.status, code: body?.code || null, body },
    );
  }
  return body || {};
}

function demoPath(demoUuid) {
  return `/demos/${encodeURIComponent(demoUuid)}`;
}

export async function loadDemos(serviceCode) {
  const body = await publicRequest(serviceCode, '/demos');
  return Array.isArray(body.demos) ? body.demos : [];
}

export async function loadDemoOutputs(serviceCode, demoUuid) {
  const body = await publicRequest(serviceCode, `${demoPath(demoUuid)}/outputs`);
  return Array.isArray(body.outputs) ? body.outputs : [];
}

export function signedDemoOutput(serviceCode, demoUuid, outputId) {
  return publicRequest(serviceCode, `${demoPath(demoUuid)}/outputs/${encodeURIComponent(outputId)}/download`);
}

export function signedDemoBundle(serviceCode, demoUuid) {
  return publicRequest(serviceCode, `${demoPath(demoUuid)}/download`);
}

export async function loadDemoLatestReport(serviceCode, demoUuid) {
  try {
    const body = await publicRequest(serviceCode, `${demoPath(demoUuid)}/reports/latest`);
    return body.report || null;
  } catch (error) {
    if (error instanceof WebserverApiError && error.code === 'REPORT_NOT_FOUND') return null;
    throw error;
  }
}

export function signedDemoReport(serviceCode, demoUuid, reportUuid) {
  return publicRequest(serviceCode, `${demoPath(demoUuid)}/reports/${encodeURIComponent(reportUuid)}/download`);
}
